import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAppStore } from '../App';
import Chat from './Chat';
import SudoBanner from './SudoBanner';
import MasqueradeBanner from './MasqueradeBanner';
import ViewedByAdminAlert from './ViewedByAdminAlert';

interface LayoutProps {
 children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
 const location = useLocation();
 const navigate = useNavigate();
 const { user, hasNewMail, unreadInboxCount, logout, sudo, viewedByAdmin, viewedByAdminDismissed, masquerade } = useAppStore();
 const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
 const [isAdminMenuOpen, setAdminMenuOpen] = useState(false);
 // Advanced 그룹(모패밀리·캠페인·영상·스크린·거래내역) 접힘 — 기본 접힘, 계정에 영속
 // (mopeers.ui_prefs, 112). 현재 경로가 그룹 안이면 저장값과 무관하게 펼쳐 보인다.
 const [advancedOpen, setAdvancedOpen] = useState(false);
 useEffect(() => {
  fetch('/mopeer/ui-prefs', { credentials: 'include' })
   .then(r => r.json())
   .then(j => { if (j?.success) setAdvancedOpen(!!j.data?.advancedOpen); })
   .catch(() => { /* 기본 접힘 유지 */ });
 }, []);
 const ADVANCED_PATHS = ['/mofamily', '/campaign', '/mofile', '/moclient', '/market/transactions'];
 const advancedActive = ADVANCED_PATHS.some(p => location.pathname.startsWith(p));
 const showAdvanced = advancedOpen || advancedActive;
 const toggleAdvanced = () => {
  const next = !showAdvanced;
  setAdvancedOpen(next);
  fetch('/mopeer/ui-prefs', {
   method: 'PUT', credentials: 'include',
   headers: { 'Content-Type': 'application/json' },
   body: JSON.stringify({ advancedOpen: next }),
  }).catch(() => { /* 저장 실패는 무시 — 다음 토글에 재시도 */ });
 };
 // 가이드 하위메뉴 — 가이드 페이지에 있을 때는 접혀서 활성 항목이 숨지 않도록 처음부터 펼친다.
 const [isGuideMenuOpen, setGuideMenuOpen] = useState(
 location.pathname === '/device-guide' || location.pathname === '/plugin-guide'
 );
 const adminSubmenuRef = useRef<HTMLUListElement>(null);

 // 페이지 이동 시 모바일 메뉴 닫기
 useEffect(() => {
 setMobileMenuOpen(false);
 }, [location.pathname]);

 // 관리자 하위메뉴가 펼쳐지면, 하위 항목이 다 보이도록 사이드바를 아래로 스크롤.
 // (모바일에서 아래쪽 항목이 접힌 채로 잘려 보이던 문제.) 렌더 후 실행되도록 다음 프레임에.
 useEffect(() => {
 if (!isAdminMenuOpen) return;
 const id = requestAnimationFrame(() => {
 adminSubmenuRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
 });
 return () => cancelAnimationFrame(id);
 }, [isAdminMenuOpen]);

 const handleLogout = async () => {
 await logout();
 navigate('/');
 };

 const isActive = (path: string) => location.pathname === path;

 return (
 <div className="min-h-screen bg-white">
 {sudo && <SudoBanner sudo={sudo} />}
 {masquerade && <MasqueradeBanner accountName={masquerade.accountName} />}
 {viewedByAdmin && !viewedByAdminDismissed && <ViewedByAdminAlert info={viewedByAdmin} />}
 {/* Header — sudo 배너가 있으면 8px 아래로 */}
 <header className={`bg-white border-b border-gray-200 fixed left-0 right-0 z-40 h-16 ${sudo ? 'top-8' : 'top-0'}`}>
 <div className="flex justify-between items-center h-full px-4 md:px-6">
 <div className="flex items-center gap-4">
 {/* Mobile menu button */}
 <button
 onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
 className="md:hidden p-2 hover:bg-gray-100"
 >
 <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
 d="M4 6h16M4 12h16M4 18h16" />
 </svg>
 </button>
 
 {/* Logo */}
 <Link to="/dashboard" className="flex items-center text-xl md:text-2xl font-bold text-gray-800">
 <img src="/static/MOC-arrowEar.png" alt="MOcombinator" className="w-8 h-8 mr-2" />
 <span className="hidden sm:inline">MOcombinator</span>
 <span className="sm:hidden">MOc</span>
 </Link>
 </div>
 
 {/* Right side */}
 <div className="flex items-center gap-4">
 <span className="text-sm text-gray-600 hidden md:inline">
 {user?.name || user?.email}
 </span>
 
 {/* Profile picture with mail notification */}
 <div className="relative">
 <button
 onClick={() => navigate('/profile')}
 className="block hover:opacity-80 transition-opacity"
 >
 {user?.profilePicture ? (
 <img 
 src={user.profilePicture} 
 alt="Profile" 
 className="w-8 h-8 rounded-full border-2 border-gray-300"
 />
 ) : (
 <div className="w-8 h-8 rounded-full bg-gray-400 flex items-center justify-center text-white text-sm font-medium">
 {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
 </div>
 )}
 </button>
 
 {/* 메시지 알림 dot — unread 가 1 이상이면 표시 */}
 {unreadInboxCount > 0 && (
 <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500"></span>
 )}
 </div>
 
 <button
 onClick={handleLogout}
 className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1.5 text-sm"
 >
 로그아웃
 </button>
 </div>
 </div>
 </header>

 <div className={`flex h-screen ${sudo ? 'pt-24' : 'pt-16'}`}>
 {/* Sidebar */}
 <aside className={`${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 transition-transform duration-300 fixed md:static ${sudo ? 'top-24' : 'top-16'} bottom-0 md:top-auto md:bottom-auto md:h-full w-64 bg-white border-r border-gray-200 z-30 overflow-y-auto`}>
 <nav className="p-4">
 <ul className="space-y-1">
 <li>
 <Link 
 to="/dashboard"
 className={`block px-4 py-3 transition ${
 isActive('/dashboard') 
 ? 'bg-gray-100 text-gray-900 font-medium' 
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 대시보드 개요
 </Link>
 </li>

 {/* BASIC 모드(격리) — 제거 시 이 li 만 삭제 */}
 <li>
 <Link
 to="/basic"
 className={`block px-4 py-3 transition ${
 isActive('/basic')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 간편 모드
 </Link>
 </li>

 {/* Advanced 그룹 — 접힘 상태는 계정(ui_prefs)에 영속. 기본 접힘. */}
 <li>
 <button
 type="button"
 onClick={toggleAdvanced}
 className="w-full flex items-center justify-between px-4 py-3 transition hover:bg-gray-100 text-gray-700"
 >
 <span>Advanced</span>
 <span className="text-gray-400 text-xs">{showAdvanced ? '▾' : '▸'}</span>
 </button>
 </li>

 {showAdvanced && (<>
 <li>
 <Link
 to="/mofamily"
 className={`block pl-8 pr-4 py-3 transition flex items-center justify-between ${
 isActive('/mofamily') || location.pathname.startsWith('/mofamily/')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 <span>모패밀리</span>
 <img src="/static/MoFamily.png" alt="MoFamily" className="w-10 h-10 object-contain" />
 </Link>
 </li>

 <li>
 <Link
 to="/campaign"
 className={`block pl-8 pr-4 py-3 transition ${
 isActive('/campaign')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 광고 캠페인
 </Link>
 </li>

 {/* 티켓 메뉴는 임시로 숨김
 <li>
 <Link 
 to="/ticket"
 className={`block px-4 py-3 transition ${
 isActive('/ticket') 
 ? 'bg-gray-100 text-gray-900 font-medium' 
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 티켓
 </Link>
 </li>
 */}
 
 <li>
 <Link
 to="/mofile"
 className={`block pl-8 pr-4 py-3 transition ${
 isActive('/mofile')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 영상
 </Link>
 </li>

 <li>
 <Link
 to="/moclient"
 className={`block pl-8 pr-4 py-3 transition ${
 isActive('/moclient')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 스크린
 </Link>
 </li>

 <li>
 <Link
 to="/market/transactions"
 className={`block pl-8 pr-4 py-3 transition ${
 isActive('/market/transactions')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 거래 내역
 </Link>
 </li>

 {/* 웹 플레이어(OpenPlayer) — 정적 페이지라 React 라우트가 아닌 새 탭 링크. 화면 기기에서
     이 주소를 열면 등록 QR이 뜨고 재생 클라이언트가 된다. */}
 <li>
 <a
 href="/static/openplayer/index.html"
 target="_blank"
 rel="noopener"
 title="화면(사이니지) 기기 전용 재생 클라이언트. 등록하면 재생 로그·정산 통계에 반영되니, 실제 화면으로 쓸 전용 기기에서만 여세요."
 className="block pl-8 pr-4 py-3 transition hover:bg-gray-100 text-gray-700 flex items-center justify-between"
 >
 <span>웹 플레이어 (화면 기기용)</span>
 <span className="text-gray-300 text-xs">↗</span>
 </a>
 </li>
 </>)}

 <li>
 <Link
 to="/inbox"
 className={`block px-4 py-3 transition flex items-center justify-between ${
 location.pathname.startsWith('/inbox')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 <span>메시지</span>
 {unreadInboxCount > 0 && (
 <span className="bg-red-500 text-white text-xs px-2 py-0.5 ml-2">
 {unreadInboxCount > 9 ? '9+' : unreadInboxCount}
 </span>
 )}
 </Link>
 </li>

 <li className="pt-2 mt-2 border-t border-gray-100">
 <Link
 to="/market"
 className={`block px-4 py-3 transition ${
 isActive('/market')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 마켓
 </Link>
 </li>

 <li>
 <Link
 to="/profile"
 className={`block px-4 py-3 transition ${
 isActive('/profile')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 설정
 </Link>
 </li>

 <li>
 <div className="relative">
 <button
 onClick={() => setGuideMenuOpen(!isGuideMenuOpen)}
 className={`w-full flex items-center justify-between px-4 py-3 transition ${
 isActive('/device-guide') || isActive('/plugin-guide')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 <span>가이드</span>
 <svg
 className={`w-4 h-4 transition-transform ${isGuideMenuOpen ? 'rotate-180' : ''}`}
 fill="none"
 stroke="currentColor"
 viewBox="0 0 24 24"
 >
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
 </svg>
 </button>

 {isGuideMenuOpen && (
 <ul className="mt-1 space-y-1 pl-4">
 <li>
 <Link
 to="/device-guide"
 className={`block px-4 py-3 transition ${
 isActive('/device-guide')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 화면 설치
 </Link>
 </li>
 <li>
 <Link
 to="/plugin-guide"
 className={`block px-4 py-3 transition ${
 isActive('/plugin-guide')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 인스타그램 플러그인
 </Link>
 </li>
 </ul>
 )}
 </div>
 </li>

 {(user?.isAdmin || user?.role === 'admin') && (
 <li>
 <div className="relative">
 <button
 onClick={() => setAdminMenuOpen(!isAdminMenuOpen)}
 className={`w-full flex items-center justify-between px-4 py-3 transition ${
 location.pathname.startsWith('/admin')
 ? 'bg-gray-100 text-gray-900 font-medium' 
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 <span>관리자</span>
 <svg 
 className={`w-4 h-4 transition-transform ${isAdminMenuOpen ? 'rotate-180' : ''}`} 
 fill="none" 
 stroke="currentColor" 
 viewBox="0 0 24 24"
 >
 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
 </svg>
 </button>
 
 {/* Admin submenu */}
 {isAdminMenuOpen && (
 <ul ref={adminSubmenuRef} className="mt-1 space-y-1 pl-4">
 <li>
 <Link
 to="/admin/users"
 className={`block px-4 py-3 transition ${
 isActive('/admin/users')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 회원
 </Link>
 </li>
 <li>
 <Link
 to="/admin/market"
 className={`block px-4 py-3 transition ${
 isActive('/admin/market')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 마켓 신고
 </Link>
 </li>
 <li>
 <Link
 to="/admin/device-setup"
 className={`block px-4 py-3 transition ${
 isActive('/admin/device-setup')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 장비 설치 가이드
 </Link>
 </li>
 <li>
 <Link
 to="/admin/proposals"
 className={`block px-4 py-3 transition ${
 isActive('/admin/proposals')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 제안 페이지
 </Link>
 </li>
 <li>
 <Link
 to="/admin/moboard"
 className={`block px-4 py-3 transition ${
 isActive('/admin/moboard')
 ? 'bg-gray-100 text-gray-900 font-medium'
 : 'hover:bg-gray-100 text-gray-700'
 }`}
 >
 MoBoard 수집
 </Link>
 </li>
 <li>
 {/* 외부 정적 HTML 페이지 — SPA 라우팅 우회를 위해 a href 로 연결 */}
 <a
 href="/admin/capital-simulator"
 className="block px-4 py-3 transition hover:bg-gray-100 text-gray-700"
 >
 자본 시뮬레이터
 </a>
 </li>
 </ul>
 )}
 </div>
 </li>
 )}
 </ul>
 </nav>
 </aside>

 {/* Mobile menu overlay */}
 {isMobileMenuOpen && (
 <div 
 className="fixed inset-0 bg-black bg-opacity-50 z-20 md:hidden"
 onClick={() => setMobileMenuOpen(false)}
 ></div>
 )}

 {/* Main content */}
 <main className="flex-1 overflow-y-auto">
 <div className="px-2 md:px-3 py-1 md:py-3">
 {children}
 </div>
 <footer className="border-t border-gray-100 py-4 px-4 md:px-8 mt-auto">
 <div className="flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-400">
 <span>Contact us: ear@mocombinator.com</span>
 <a href="/policy/admin-access" className="hover:text-gray-600 hover:underline">관리자 view-as 정책</a>
 <span>&copy; 2024-2026 MOCombinator</span>
 </div>
 </footer>
 </main>
 </div>

 {/* Chat Component */}
 <Chat />
 </div>
 );
};

export default Layout;